import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Cpu } from "lucide-react";
import EngineRating from "./EngineRating";
import SoundHistogram from "./SoundHistogram";
import SoundTypeAnalysis from "./SoundTypeAnalysis";
import WisedriveAnalysis from "./WisedriveAnalysis";
import PredictiveChart from "./PredictiveChart";
import ValidationCard from "./ValidationCard";
import OBDInsightCard from "./OBDInsightCard";
import ExpertRecommendation from "./ExpertRecommendation";
import AdvancedDiagnostic from "./AdvancedDiagnostic";
import AIDiagnosisSection from "./AIDiagnosisSection";
import BankAnalysis from "./BankAnalysis";
import InsuranceAnalysis from "./InsuranceAnalysis";

export default function ResultsDashboard({ result }) {
  if (!result) return null;

  return (
    <div data-testid="results-dashboard">
      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="bg-slate-100 rounded-sm h-12 mb-6 p-1 w-full grid grid-cols-2">
          <TabsTrigger 
            value="summary" 
            className="text-base font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm h-full"
            data-testid="tab-summary-report"
          >
            <FileText size={16} className="mr-2" /> Summary Report
          </TabsTrigger>
          <TabsTrigger 
            value="technical" 
            className="text-base font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm h-full"
            data-testid="tab-technical-analysis"
          >
            <Cpu size={16} className="mr-2" /> Technical AI Analysis
          </TabsTrigger>
        </TabsList>

        {/* SUMMARY REPORT TAB */}
        <TabsContent value="summary" className="space-y-4">
          {/* Engine Health Rating + Sound Validation + OBD-II Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <EngineRating rating={result.engine_rating} />
            <ValidationCard validation={result.validation} />
            <OBDInsightCard
              obdData={result.obd_data}
              hasObd={result.has_obd_data}
            />
          </div>

          {/* Engine Sound Type Analysis */}
          <SoundTypeAnalysis data={result.sound_classification} />

          {/* MFCWL Engine Sound Analysis */}
          <WisedriveAnalysis aiAnalysis={result.ai_analysis} />

          {/* Predictive Charts - WITH/WITHOUT Maintenance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <PredictiveChart
              title="WITH Regular Maintenance"
              data={result.predictive_data?.maintained}
              currentKm={result.predictive_data?.current_km}
              type="maintained"
            />
            <PredictiveChart
              title="WITHOUT Maintenance"
              data={result.predictive_data?.unmaintained}
              currentKm={result.predictive_data?.current_km}
              type="unmaintained"
            />
          </div>

          {/* MFCWL Expert Assessment (Warranty + Repairs + Market Value) */}
          <ExpertRecommendation
            predictiveData={result.predictive_data}
            carDetails={result.car_details}
            engineRating={result.engine_rating}
          />

          {/* Bank / Lender Analysis Section */}
          <BankAnalysis data={result.financial_analysis?.bank_analysis} />

          {/* Insurance Company Analysis Section */}
          <InsuranceAnalysis data={result.financial_analysis?.insurance_analysis} />
        </TabsContent>

        {/* TECHNICAL AI ANALYSIS TAB */}
        <TabsContent value="technical" className="space-y-4">
          {/* Sound Analysis (Histogram, Waveform, Energy) */}
          <SoundHistogram 
            data={result.histogram_data} 
            histogramImage={result.histogram_image}
            waveformImage={result.waveform_image}
          />

          {/* Advanced Engine Diagnostic - ECG-like Spectrogram Analysis */}
          <AdvancedDiagnostic 
            spectrogramDiagnostic={result.spectrogram_diagnostic}
            spectrogramImage={result.diagnostic_spectrogram_image}
            hasTurbo={result.car_details?.has_turbo || false}
          />

          {/* AI Predictive Diagnosis - Separate Section */}
          <AIDiagnosisSection aiDiagnosis={result.ai_diagnosis} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
